import type { InvoiceDetail } from '../../types/invoices';
import {
  formatInvoiceCurrency,
  formatInvoiceDate,
  formatInvoiceQty,
} from './InvoiceDetailModal.utils';

/**
 * Branded, print-friendly invoice sheet (header, meta grid, addresses,
 * line table, totals). Rendered by both the standalone InvoiceDetailModal
 * and the order-detail invoice section, so it lives in its own module.
 */
export const InvoiceSheet: React.FC<{ invoice: InvoiceDetail }> = ({
  invoice,
}) => {
  const isCreditMemo = invoice.kind === 'credit_memo';
  return (
    <div style={{ fontSize: 12, color: '#111827' }}>
      {/* Branded header */}
      <div
        style={{
          background: isCreditMemo ? '#7f1d1d' : '#1e3a8a',
          color: '#fff',
          padding: '18px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 24,
        }}
      >
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '0.04em' }}>
            ChemMasters Inc.
          </div>
          <div style={{ fontSize: 10, opacity: 0.75, marginTop: 4 }}>
            300 Edwards St · Madison, OH 44057 · (800) 486-7866
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, opacity: 0.75, letterSpacing: '0.08em' }}>
            {isCreditMemo ? 'CREDIT MEMO' : 'INVOICE'}
          </div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>
            #{invoice.invoice_no}
          </div>
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
            {formatInvoiceDate(invoice.invoice_date)}
          </div>
        </div>
      </div>

      {/* Meta grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
          padding: '16px 24px',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <MetaCell label="Sales Order" value={invoice.sales_order_no} />
        <MetaCell label="Customer PO" value={invoice.customer_po_no} />
        <MetaCell label="Customer #" value={invoice.customer_no} />
        <MetaCell label="Salesperson" value={invoice.salesperson_no} />
        <MetaCell label="Terms" value={invoice.terms_code} />
        <MetaCell
          label="Due"
          value={formatInvoiceDate(invoice.invoice_due_date)}
        />
        <MetaCell label="Ship Via" value={invoice.ship_via} />
        <MetaCell label="FOB" value={invoice.fob} />
        <MetaCell label="Warehouse" value={invoice.warehouse_code} />
        <MetaCell
          label="Ship Date"
          value={formatInvoiceDate(invoice.ship_date)}
        />
        <MetaCell
          label="Order Date"
          value={formatInvoiceDate(invoice.order_date)}
        />
        {invoice.comment && <MetaCell label="Comment" value={invoice.comment} />}
      </div>

      {/* Addresses */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          padding: '16px 24px',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <AddressBlock label="Bill To" addr={invoice.bill_to} />
        <AddressBlock label="Ship To" addr={invoice.ship_to} />
      </div>

      {/* Line items */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <Th>Item</Th>
            <Th>Description</Th>
            <Th align="right">Qty</Th>
            <Th>UOM</Th>
            <Th align="right">Unit Price</Th>
            <Th align="right">Extension</Th>
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((line) => (
            <tr key={line.detail_seq_no}>
              <Td>{line.item_code}</Td>
              <Td>
                {line.description}
                {line.comment_text && (
                  <div style={{ color: '#6b7280', fontSize: 11 }}>
                    {line.comment_text}
                  </div>
                )}
              </Td>
              <Td align="right">
                {formatInvoiceQty(
                  line.quantity_shipped ?? line.quantity_ordered,
                )}
              </Td>
              <Td>{line.unit_of_measure}</Td>
              <Td align="right">{formatInvoiceCurrency(line.unit_price)}</Td>
              <Td align="right">
                {formatInvoiceCurrency(line.extension_amt)}
              </Td>
            </tr>
          ))}
          {invoice.lines.length === 0 && (
            <tr>
              <td
                colSpan={6}
                style={{
                  padding: 24,
                  textAlign: 'center',
                  color: '#6b7280',
                }}
              >
                No line items on this invoice.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Totals */}
      <div
        style={{
          padding: '12px 24px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto auto',
            gap: '4px 24px',
            margin: 0,
            fontSize: 13,
            minWidth: 280,
          }}
        >
          <TotalRow label="Taxable sales" value={invoice.taxable_sales_amt} />
          <TotalRow
            label="Non-taxable sales"
            value={invoice.non_taxable_sales_amt}
          />
          <TotalRow label="Freight" value={invoice.freight_amt} />
          <TotalRow label="Sales tax" value={invoice.sales_tax_amt} />
          {invoice.discount_amt &&
            Number(invoice.discount_amt) !== 0 && (
              <TotalRow label="Discount" value={invoice.discount_amt} />
            )}
          <TotalRow label="Total" value={invoice.total_amt} grand />
        </dl>
      </div>
    </div>
  );
};

const MetaCell: React.FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) => (
  <div>
    <div
      style={{
        color: '#9ca3af',
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: 2,
      }}
    >
      {label}
    </div>
    <div style={{ color: '#111827', fontWeight: 500 }}>{value || '—'}</div>
  </div>
);

const AddressBlock: React.FC<{
  label: string;
  addr: InvoiceDetail['bill_to'];
}> = ({ label, addr }) => (
  <div>
    <div
      style={{
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: '#6b7280',
        marginBottom: 4,
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    {addr.name && (
      <div style={{ fontWeight: 600, fontSize: 13 }}>{addr.name}</div>
    )}
    {[addr.address1, addr.address2, addr.address3]
      .filter(Boolean)
      .map((line, i) => (
        <div key={i} style={{ color: '#374151' }}>
          {line}
        </div>
      ))}
    {(addr.city || addr.state || addr.zip_code) && (
      <div style={{ color: '#374151' }}>
        {[addr.city, addr.state, addr.zip_code].filter(Boolean).join(', ')}
      </div>
    )}
  </div>
);

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({
  children,
  align = 'left',
}) => (
  <th
    style={{
      padding: '8px 12px',
      textAlign: align,
      background: '#f9fafb',
      color: '#6b7280',
      fontSize: 10,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      fontWeight: 600,
      borderBottom: '1px solid #e5e7eb',
    }}
  >
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({
  children,
  align = 'left',
}) => (
  <td
    style={{
      padding: '8px 12px',
      textAlign: align,
      borderBottom: '1px solid #f3f4f6',
      fontVariantNumeric: align === 'right' ? 'tabular-nums' : undefined,
    }}
  >
    {children}
  </td>
);

const TotalRow: React.FC<{
  label: string;
  value: string | number | null;
  grand?: boolean;
}> = ({ label, value, grand = false }) => (
  <>
    <dt
      style={{
        color: grand ? '#111827' : '#6b7280',
        fontSize: grand ? 16 : 13,
        fontWeight: grand ? 700 : 400,
        borderTop: grand ? '1px solid #e5e7eb' : undefined,
        paddingTop: grand ? 6 : undefined,
        marginTop: grand ? 4 : undefined,
      }}
    >
      {label}
    </dt>
    <dd
      style={{
        margin: 0,
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
        fontWeight: grand ? 700 : 500,
        fontSize: grand ? 16 : 13,
        color: grand ? '#111827' : undefined,
        borderTop: grand ? '1px solid #e5e7eb' : undefined,
        paddingTop: grand ? 6 : undefined,
        marginTop: grand ? 4 : undefined,
      }}
    >
      {formatInvoiceCurrency(value)}
    </dd>
  </>
);
